import { Set, Iterable } from 'immutable';
import SearchResults from '../../../Models/SearchResults';
import type { ID, IDRecord, ReduxAction, SearchState, SearchType } from '../../../types';
import { actionTypes, identifierForSearchType } from '@actions/search';
import Contact from '@models/Contact';
import Occasion from '@support/Occasion';

type StateUpdater = (state: SearchState, payload?: any) => SearchState;

const initialSearchState = (searchType: SearchType, initialSliceState: Partial<SearchState>) => ({
  type: searchType,
  term: '',
  results: new SearchResults(),
  sorting: {},
  deletedIDCacheToExpire: Set<ID>(),
  ...initialSliceState,
});

// TODO: this should be deprecated. Any consumers should switch to putting results in contacts collection and reading from there
export const searchResponseToContactTuples = (
  response: any,
  includeSnippetResult?: boolean,
): [ID, Contact][] =>
  (response?.hits?.hits || []).map((contact: any) => [
    String(contact._source.objectID),
    new Contact({
      ...contact._source,
      _snippetResult: includeSnippetResult ? contact.highlight : undefined,
    }),
  ]);

export const searchResponseToContactIDs = (response): [ID, IDRecord][] =>
  (response?.hits?.hits || []).map((contact: any) => {
    const id = String(contact._source.objectID);
    return [id, { id }];
  });

// StateUpdaters that are re-used by each SearchType reducer
export const fetchSearchResultsRequested: StateUpdater = (state, payload) => ({
  ...state,
  results: state.results.copyWith({ isFetching: true, hitsPerPage: payload?.hitsPerPage }),
});

export const fetchSearchResultsSucceeded: StateUpdater = (state, payload) => {
  // If payload.response.page > 0, concat results. otherwise, overwrite results.
  const isFirstPage = payload.response.page === 1;
  const resultCountOverride = {};

  let items: [ID, IDRecord][] | Iterable<ID, IDRecord> = searchResponseToContactIDs(
    payload.response,
  );

  if (!isFirstPage) {
    items = state.results.items.concat(items);
  }

  // if ES returns no hits, set results to 0
  if (!payload.response.hits) {
    return {
      ...state,
      results: state.results.copyWith({
        found: 0,
        isFetching: false,
        items,
        updated: new Occasion(),
      }),
    };
  }

  if (payload.response.hits.hits.length === 0) {
    // @ts-ignore
    resultCountOverride['found'] = items.length || items.size || 0;
  }

  return {
    ...state,
    results: state.results.copyWith({
      ...payload.response,
      ...resultCountOverride,
      isFetching: false,
      items,
      updated: new Occasion(),
      deletedIDs: isFirstPage
        ? state.results.deletedIDs.subtract(state.deletedIDCacheToExpire)
        : state.results.deletedIDs,
    }),
    deletedIDCacheToExpire: isFirstPage ? Set() : state.deletedIDCacheToExpire,
  };
};

export const fetchSearchResultsFailed: StateUpdater = (state, payload) => ({
  ...state,
  results: state.results.copyWith({ isFetching: false, error: payload }),
});

export const clearSearchTerm: StateUpdater = (state) => ({
  ...state,
  term: '',
  results: state.results.copyWith({
    isFetching: false,
    items: [],
    updated: new Occasion(),
  }),
});

export const setSearchSorting: StateUpdater = (state, payload) => ({
  ...state,
  sorting: {
    name: payload.type,
    direction: payload.direction,
  },
});

export const setUserInSearchCollection: StateUpdater = (state, payload) => ({
  ...state,
  results: state.results.copyWith({ user: payload }),
});

// Map of StateUpdaters used by the main search and the allContactsSearch that
// update the list of results in response to some contact management action (merge or delete)
export const contactManagementUpdaters: { [K in string]: StateUpdater } = {
  DELETE_CONTACT_REQUESTED: (state, payload) => ({
    ...state,
    results: state.results.copyWith({
      deletedIDs: state.results.deletedIDs.add(payload.id.toString()),
      updated: new Occasion(),
    }),
  }),
  DELETE_CONTACT_FAILED: (state, payload) => ({
    ...state,
    results: state.results.copyWith({
      deletedIDs: state.results.deletedIDs.delete(payload.id.toString()),
      updated: new Occasion(),
    }),
  }),
  DELETE_CONTACT_BATCH_REQUESTED: (state, payload) => ({
    ...state,
    results: state.results.copyWith({
      deletedIDs: state.results.deletedIDs.union(payload.contact_ids),
      updated: new Occasion(),
    }),
  }),
  DELETE_CONTACT_BATCH_FAILED: (state, payload) => ({
    ...state,
    results: state.results.copyWith({
      deletedIDs: state.results.deletedIDs.subtract(payload.contact_ids),
      updated: new Occasion(),
    }),
  }),
  EXPIRE_DELETED_CONTACTS_CACHE: (state, payload) => ({
    ...state,
    deletedIDCacheToExpire: state.deletedIDCacheToExpire.union(payload.contact_ids),
  }),
  EXPIRE_MERGED_CONTACTS_CACHE: (state, payload) => ({
    ...state,
    deletedIDCacheToExpire: state.deletedIDCacheToExpire.union(payload.mergedContactIDs),
  }),

  MERGE_CONTACT_BATCH_SUCCEEDED: (state, payload) => {
    const mergedIntoID = payload?.response?.contact?.objectID;
    let deletedIDsPlusMerged = state.results.deletedIDs.union(payload.mergedContactIDs);
    // let itemsWithMerged = state.results.items;

    if (mergedIntoID) {
      deletedIDsPlusMerged = deletedIDsPlusMerged.remove(String(mergedIntoID));
      // TODO: handle this on contact collection? HMM
      // itemsWithMerged = itemsWithMerged.update(mergedIntoID, (contact) => {
      //   if (contact) {
      //     return contact.copyWith({ ...payload.response.contact, isFetching: false });
      //   }
      // });
      // itemsWithMerged = itemsWithMerged.subtract
    }

    return {
      ...state,
      results: state.results.copyWith({
        // items: itemsWithMerged,
        deletedIDs: deletedIDsPlusMerged,
        updated: new Occasion(),
      }),
    };
  },
  // Handle this in the contact collection instead of the search results
  // PROBABLY will want to map the new contact to each of the merged IDs?
  // Or that needs to happen here? IDK
  // MERGE_CONTACT_BATCH_FAILED: (state, payload) => {
  //   const itemsWithMergersUnmarked = (payload.failedMergeIDs || []).reduce(
  //     (items, failedMergeID) => {
  //       return items.update(failedMergeID, (contact) => {
  //         if (contact) {
  //           return contact.copyWith({ isFetching: false });
  //         }
  //       });
  //     },
  //     state.results.items,
  //   );

  //   return {
  //     ...state,
  //     results: state.results.copyWith({
  //       items: itemsWithMergersUnmarked,
  //       updated: new Occasion(),
  //     }),
  //   };
  // },
  RESTORE_ARCHIVED_CONTACT_BATCH_REQUESTED: (state, payload: { contactIDs: Set<ID> }) => {
    return {
      ...state,
      results: state.results.copyWith({
        deletedIDs: state.results.deletedIDs.subtract(payload.contactIDs),
      }),
    };
  },
};

export function createSearchReducer(
  searchType: SearchType,
  initialSliceState: Partial<SearchState> = {},
) {
  const identifier = identifierForSearchType[searchType];
  const initialState = initialSearchState(searchType, initialSliceState);

  return (state: SearchState = initialState, { type, payload }: ReduxAction) => {
    // If we are in "allPeopleSearch," update list of contacts in results on contact merge or delete action
    const contactsUpdater = contactManagementUpdaters[type];

    if (contactsUpdater) {
      return contactsUpdater(state, payload);
    }

    switch (type) {
      case 'LOAD_LOCAL_STORE_SUCCEEDED':
        const sorting =
          payload.get(`${identifier}_search_sorting`.toLowerCase()) || state.sorting || {};
        return { ...state, sorting };

      case actionTypes[`SET_${identifier}_SEARCH_TERM`]:
        return { ...state, term: payload.term };

      case `FETCH_${identifier}_SEARCH_RESULTS_REQUESTED`:
        return fetchSearchResultsRequested(state, payload);

      case `FETCH_${identifier}_SEARCH_RESULTS_SUCCEEDED`:
        return fetchSearchResultsSucceeded(state, payload);

      case `FETCH_${identifier}_SEARCH_RESULTS_FAILED`:
        return fetchSearchResultsFailed(state, payload);

      case `SET_${identifier}_SEARCH_SORTING`:
        return setSearchSorting(state, payload);

      case actionTypes[`CLEAR_${identifier}_SEARCH_TERM`]:
        return clearSearchTerm(state, payload);

      case 'SET_USER':
        return setUserInSearchCollection(state, payload);

      default:
        return state;
    }
  };
}
